import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, MailX, CheckCircle2 } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

type State =
  | { kind: "validating" }
  | { kind: "ready" }
  | { kind: "already" }
  | { kind: "invalid"; message: string }
  | { kind: "submitting" }
  | { kind: "done" };

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

const Unsubscribe = () => {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState<State>({ kind: "validating" });

  useEffect(() => {
    document.title = "Unsubscribe | The Emerging Market";
    if (!token) {
      setState({ kind: "invalid", message: "Missing unsubscribe token." });
      return;
    }
    (async () => {
      try {
        const resp = await fetch(
          `${SUPABASE_URL}/functions/v1/handle-email-unsubscribe?token=${encodeURIComponent(token)}`,
          { headers: { apikey: SUPABASE_ANON_KEY } },
        );
        const data = await resp.json();
        if (!resp.ok) {
          setState({
            kind: "invalid",
            message: data.error ?? "This link is no longer valid.",
          });
          return;
        }
        if (data.valid === false && data.reason === "already_unsubscribed") {
          setState({ kind: "already" });
          return;
        }
        setState({ kind: "ready" });
      } catch {
        setState({
          kind: "invalid",
          message: "Couldn't reach the server. Try again later.",
        });
      }
    })();
  }, [token]);

  const confirm = async () => {
    if (!token) return;
    setState({ kind: "submitting" });
    const { data, error } = await supabase.functions.invoke(
      "handle-email-unsubscribe",
      { body: { token } },
    );
    if (error) {
      setState({ kind: "invalid", message: error.message });
      return;
    }
    if ((data as any)?.success || (data as any)?.reason === "already_unsubscribed") {
      setState({ kind: "done" });
    } else {
      setState({ kind: "invalid", message: "Couldn't process your request." });
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="container mx-auto max-w-md px-6 pt-32 pb-20">
        <div className="bg-card border border-border rounded-2xl p-8 text-center shadow-card">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            {state.kind === "done" || state.kind === "already" ? (
              <CheckCircle2 className="h-6 w-6" />
            ) : (
              <MailX className="h-6 w-6" />
            )}
          </div>

          {state.kind === "validating" && (
            <>
              <h1 className="font-display text-xl font-semibold mb-2">
                Checking your link…
              </h1>
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground mx-auto" />
            </>
          )}

          {state.kind === "ready" && (
            <>
              <h1 className="font-display text-xl font-semibold mb-2">
                Unsubscribe from emails
              </h1>
              <p className="text-sm text-muted-foreground mb-6">
                We'll stop sending you emails from The Emerging Market.
              </p>
              <Button onClick={confirm} className="w-full">
                Confirm unsubscribe
              </Button>
            </>
          )}

          {state.kind === "submitting" && (
            <>
              <h1 className="font-display text-xl font-semibold mb-2">
                Updating your preferences…
              </h1>
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground mx-auto" />
            </>
          )}

          {state.kind === "done" && (
            <>
              <h1 className="font-display text-xl font-semibold mb-2">
                You're unsubscribed
              </h1>
              <p className="text-sm text-muted-foreground">
                You won't receive further emails from us. Changed your mind?
                Reach out to support to opt back in.
              </p>
            </>
          )}

          {state.kind === "already" && (
            <>
              <h1 className="font-display text-xl font-semibold mb-2">
                Already unsubscribed
              </h1>
              <p className="text-sm text-muted-foreground">
                This email address is no longer receiving messages from us.
              </p>
            </>
          )}

          {state.kind === "invalid" && (
            <>
              <h1 className="font-display text-xl font-semibold mb-2">
                Link is invalid
              </h1>
              <p className="text-sm text-muted-foreground">{state.message}</p>
            </>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default Unsubscribe;